import './BatchAnswerQuestions.scss';
import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../../../design/buttons/ButtonWithLoader';
import { S_ErrorFeedback } from '../../../../../../design/feedback/errorFeedback/S_ErrorFeedback';
import { S_Modal } from '../../../../../../design/modals/modal/S_Modal';
import { ProgressBar } from '../../../../../components/ProgressBar';
import { QuestionWithAnswers } from '../../../../annotation/components/annotations/QuestionWithAnswers';
import { IQuestionModel } from '../../../../annotation/question.model';

interface IProps {
  isModalOpen: boolean;
  disabled: boolean;
  projectHasNoQuestions: boolean;
  batchAnswerQuestionsRequestInProgress: boolean;
  batchAnswerBatchesCount: number;
  batchAnswerSuccessfulBatches: number;
  batchAnswerFailedBatches: number;
  questions: ReadonlyArray<IQuestionModel>;
  batchAnswerQuestionsModalError: TranslationKey | undefined;
  onAnswered: (questionId: string, answer: string) => void;
  onSubmit: () => Promise<void>;
  onModalShow: () => void;
  onModalClose: () => void;
}

const QuestionsFooter = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  return (
    <>
      <Button
        color='neutral'
        variant='outlined'
        onClick={props.onModalClose}
      >
        {t('cancel')}
      </Button>
      <ButtonWithLoader
        loading={props.batchAnswerQuestionsRequestInProgress}
        onClick={props.onSubmit}
      >
        <div>{t('submit')}</div>
      </ButtonWithLoader>
    </>
  );
});

const InProgressFooter = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  return (
    <Button
      variant='outlined'
      onClick={props.onModalClose}
    >
      {t('close')}
    </Button>
  );
});

const InProgressBody = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const isAnsweringFinished =
    props.batchAnswerBatchesCount !== 0 && props.batchAnswerSuccessfulBatches + props.batchAnswerFailedBatches === props.batchAnswerBatchesCount;

  return (
    <>
      <div>{t('answering_started')}</div>
      <div className='batch-progress'>
        <b>{t(isAnsweringFinished ? 'answering_done' : 'answering_progress')}</b>
      </div>
      {props.batchAnswerBatchesCount === 0 ? (
        <ProgressBar
          value={0}
          max={100}
        />
      ) : (
        <ProgressBar
          value={props.batchAnswerSuccessfulBatches + props.batchAnswerFailedBatches}
          max={props.batchAnswerBatchesCount}
        />
      )}
      {props.batchAnswerFailedBatches > 0 && <span>{t('batch_answer_questions_failed_error')}</span>}
    </>
  );
});

const QuestionsBody = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  return (
    <div>
      {t('answer_questions_modal_header')}
      <div className='batch-answer-question-list'>
        {props.questions.map((q) => (
          <QuestionWithAnswers
            key={q.id}
            question={q}
            disabled={false}
            isAttribute={false}
            onAnswered={props.onAnswered}
          />
        ))}
      </div>
      {props.batchAnswerQuestionsModalError !== undefined && <S_ErrorFeedback>{t(props.batchAnswerQuestionsModalError)}</S_ErrorFeedback>}
    </div>
  );
});

export const BatchAnswerQuestions = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  let titleKey: TranslationKey = 'answer_questions_for_selected_images';
  if (props.projectHasNoQuestions) {
    titleKey = 'project_has_no_questions';
  } else if (props.disabled) {
    titleKey = 'at_least_on_image_must_be_selected';
  }

  return (
    <>
      <Button
        disabled={props.disabled}
        onClick={props.onModalShow}
        id='answer-questions-button'
        title={t(titleKey)}
      >
        {t('answer_questions')}
      </Button>
      <S_Modal
        className='batch-answer-modal'
        isOpen={props.isModalOpen}
        toggle={props.onModalClose}
        headerText={t('batch_questions_modal_header')}
        buttons={props.batchAnswerQuestionsRequestInProgress ? <InProgressFooter {...props} /> : <QuestionsFooter {...props} />}
      >
        {props.batchAnswerQuestionsRequestInProgress ? <InProgressBody {...props} /> : <QuestionsBody {...props} />}
      </S_Modal>
    </>
  );
});
